import clsx from 'clsx';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { useLongPress } from '@shared/util/hooks/useLongPress';
import { ButtonViewProps, ViewProps } from './ViewProps';
import { useViewStyles } from './useViewStyles';
import classNames from './View.module.css';

export function useViewProps<T extends ViewProps | ButtonViewProps>(props: T) {
  const {
    style,
    className,
    row,
    wrap,
    shrinkWrap,
    fill,
    flex,
    fullWidth,
    fullHeight,
    relative,
    absolute,
    center,
    centerVertically,
    centerHorizontally,
    gap,
    margin,
    marginTop,
    marginLeft,
    marginRight,
    marginBottom,
    marginVertical,
    marginHorizontal,
    padding,
    paddingTop,
    paddingLeft,
    paddingRight,
    paddingBottom,
    paddingVertical,
    paddingHorizontal,
    tabIndex,
    vars,
    onLongPress,
    // View/ButtonView handle this before calling useViewProps; keep it off the DOM node.
    stopPropagation: _stopPropagation,
    ...restProps
  } = props;

  const styles = useViewStyles({
    gap,
    margin,
    marginTop,
    marginLeft,
    marginRight,
    marginBottom,
    marginVertical,
    marginHorizontal,
    padding,
    paddingTop,
    paddingLeft,
    paddingRight,
    paddingBottom,
    paddingVertical,
    paddingHorizontal,
    flex,
    vars,
    style,
  });

  const longPressProps = useLongPress(onLongPress);
  const isButton = !isNil(props.onClick) || !isNil(longPressProps);
  const hasTabIndex = !isNil(tabIndex) || isButton;

  const classes = useMemo(() => {
    return clsx(
      classNames.view,
      row && classNames.row,
      row === 'reverse' && classNames.rowReverse,
      !row && classNames.column,
      wrap && classNames.wrap,
      shrinkWrap && classNames.shrinkWrap,
      center && classNames.center,
      centerVertically && classNames.centerVertically,
      centerHorizontally && classNames.centerHorizontally,
      fill && classNames.fill,
      fullWidth && classNames.fullWidth,
      fullHeight && classNames.fullHeight,
      relative && classNames.relative,
      absolute && classNames.absolute,
      isButton && classNames.button,
      className,
    );
  }, [
    row,
    wrap,
    shrinkWrap,
    center,
    centerVertically,
    centerHorizontally,
    fill,
    fullWidth,
    fullHeight,
    relative,
    absolute,
    isButton,
    className,
  ]);

  return {
    style: styles,
    className: classes,
    role: isButton ? 'button' : undefined,
    tabIndex: hasTabIndex ? (tabIndex ?? 0) : undefined,
    ...longPressProps,
    ...restProps,
  };
}
